/**
 * Drives the Football Wordle screen: fetches today's puzzle, holds the in-progress typed guess, derives
 * board + keyboard state, and submits guesses to the server (the source of truth for tile marks). The
 * answer never reaches the client until the attempt is complete, so we render only server-returned marks.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useApiClient, extractErrorMessage } from '../../../helpers/apiClient.helper';
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext';
import { useEconomyStore } from '../../shared/stores/economyStore';
import { useInvalidateXpSummary } from '../../economy/hooks/useXpSummary';
import { useAnalytics } from '../../analytics/useAnalytics';
import { ANALYTICS_EVENTS } from '../../analytics/events';
import { fetchTodayGame, submitGuess } from '../helpers/gamesApi';
import type { WordleTodayData, WordleGuessData, WordleStatus } from '../helpers/gamesApi';
import {
  WORDLE_GAME_KEY,
  KEY_ENTER,
  KEY_DEL,
  type WordleMark,
} from '../constants/warmup.constants';

const MARK_RANK: Record<WordleMark, number> = { absent: 0, present: 1, correct: 2 };
const STALE_TIME_MS = 60 * 1000;
/** Delay before the result overlay opens, so the board's tile-reveal animation can play first. */
const REVEAL_THEN_OVERLAY_MS = 950;

function isPlayable(status: WordleStatus): boolean {
  return status === 'NOT_STARTED' || status === 'IN_PROGRESS';
}

function deriveKeyStates(guesses: WordleTodayData['attempt']['guesses']): Record<string, WordleMark> {
  const out: Record<string, WordleMark> = {};
  for (const row of guesses) {
    row.word.split('').forEach((letter, i) => {
      const mark = row.marks[i];
      if (!out[letter] || MARK_RANK[mark] > MARK_RANK[out[letter]]) out[letter] = mark;
    });
  }
  return out;
}

export function useWordleGame() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { isLoaded, isSignedIn } = useSupabaseAuth();
  const syncCoins = useEconomyStore((s) => s.syncCoins);
  const invalidateXp = useInvalidateXpSummary();
  const { track } = useAnalytics();
  const canFetch = isLoaded && isSignedIn;

  const queryKey = useMemo(() => ['games', WORDLE_GAME_KEY, 'today'], []);

  const todayQuery = useQuery({
    queryKey,
    queryFn: () => fetchTodayGame(apiClient, WORDLE_GAME_KEY),
    enabled: canFetch,
    staleTime: STALE_TIME_MS,
  });

  const [currentInput, setCurrentInput] = useState('');
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [shakeNonce, setShakeNonce] = useState(0);
  const [coinsAwarded, setCoinsAwarded] = useState(0);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const overlayTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flashToast = useCallback((message: string) => {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 1700);
  }, []);

  // Let the board's reveal animation finish before the result overlay covers it.
  useEffect(() => () => {
    if (overlayTimer.current) clearTimeout(overlayTimer.current);
  }, []);

  const data = todayQuery.data ?? null;
  const status: WordleStatus = data?.attempt.status ?? 'NOT_STARTED';
  const length = data?.puzzle.length ?? 0;
  const maxGuesses = data?.puzzle.maxGuesses ?? 6;
  const guesses = useMemo(() => data?.attempt.guesses ?? [], [data]);

  const mutation = useMutation({
    mutationFn: (guess: string) => submitGuess(apiClient, WORDLE_GAME_KEY, guess),
    onSuccess: (result: WordleGuessData) => {
      setCurrentInput('');
      queryClient.setQueryData<WordleTodayData>(queryKey, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          attempt: result.attempt,
          streak: result.streak,
          reveal: result.reveal ?? prev.reveal,
          share: result.share ?? prev.share,
        };
      });
      if (result.status === 'WON' || result.status === 'LOST') {
        if (typeof result.coins === 'number') syncCoins(result.coins);
        invalidateXp(); // completion earns XP even when coins are daily-capped
        setCoinsAwarded(result.coinsAwarded ?? 0);
        track(ANALYTICS_EVENTS.GAME_COMPLETED, {
          game_key: WORDLE_GAME_KEY,
          won: result.status === 'WON',
          guesses_used: result.attempt.guessesUsed,
          streak: result.streak.current,
        });
        if (overlayTimer.current) clearTimeout(overlayTimer.current);
        overlayTimer.current = setTimeout(() => setOverlayOpen(true), REVEAL_THEN_OVERLAY_MS);
      }
    },
    onError: (error: unknown) => {
      flashToast(extractErrorMessage(error, 'Could not submit guess'));
      if (typeof error === 'object' && error && 'response' in error) {
        const status409 = (error as { response?: { status?: number } }).response?.status === 409;
        if (status409) void todayQuery.refetch();
      }
    },
  });

  const onKeyPress = useCallback((key: string) => {
    if (key === KEY_ENTER || key === KEY_DEL) return;
    setCurrentInput((prev) => {
      if (!isPlayable(status) || prev.length >= length) return prev;
      return prev + key.toUpperCase();
    });
  }, [status, length]);

  const onDelete = useCallback(() => {
    if (!isPlayable(status)) return;
    setCurrentInput((prev) => prev.slice(0, -1));
  }, [status]);

  const onEnter = useCallback(() => {
    if (!isPlayable(status) || mutation.isPending) return;
    if (currentInput.length !== length) {
      setShakeNonce((n) => n + 1);
      flashToast('Not enough letters');
      return;
    }
    mutation.mutate(currentInput);
  }, [status, length, currentInput, mutation, flashToast]);

  const keyStates = useMemo(() => deriveKeyStates(guesses), [guesses]);

  return {
    isLoading: todayQuery.isLoading,
    isError: todayQuery.isError,
    refetch: todayQuery.refetch,
    status,
    length,
    maxGuesses,
    guesses,
    currentInput,
    keyStates,
    shakeNonce,
    dayNumber: data?.dayNumber ?? 0,
    streak: data?.streak ?? { current: 0, max: 0, lastPlayedAt: null },
    reveal: data?.reveal ?? null,
    share: data?.share ?? null,
    guessesUsed: data?.attempt.guessesUsed ?? 0,
    coinsAwarded,
    isSubmitting: mutation.isPending,
    overlayOpen,
    setOverlayOpen,
    toast,
    flashToast,
    onKeyPress,
    onDelete,
    onEnter,
  };
}

export type UseWordleGame = ReturnType<typeof useWordleGame>;
